import { useState } from 'react';
import { Link } from 'react-router-dom';
import { wordpressApi } from '../../../api';
import { useResource } from '../../../hooks';
import type { ApplicationErrorGroup, ErrorGroupStatus } from '../../../api';
import { ErrorNotice, LoadingRows } from '../../../components/ui';
import { FilterChips } from '../../../components/primitives';
import { LevelBadge, PanelCard, QuietEmpty, SectionHeader } from '../../../components/project/ui';
import type { Level } from '../../../components/project/projectHealth';
import { relativeTime } from '../../../components/project/projectHealth';
import { errorCategoryLabel, errorGroupStatusLabel, labelFor } from '../../../labels';
import { componentLabel } from '../../../components/project/wordpressHealth';
import { useProject } from '../ProjectContext';

const levelOf: Record<string, Level> = { fatal: 'critical', critical: 'critical', error: 'critical', warning: 'warning' };

/**
 * ErrorGroupsPanel lists the WordPress and WooCommerce errors of the
 * selected environment grouped by fingerprint: one row per distinct
 * problem, however often it happened. A group that first appeared shortly
 * after a deployment or update says so as "possibly correlated" — a hint
 * about timing, never a claim of cause.
 */
export function ErrorGroupsPanel() {
  const project = useProject();
  // Read here rather than taken from the shell: the shell's copy turns a
  // failed read into an empty list, and "no errors" must never be what a
  // failed read looks like (IN14).
  const projectId = project.projectId;
  const environmentId = project.environment?.id;
  const errorGroups = useResource(
    () => (environmentId ? wordpressApi.listErrorGroups(projectId, environmentId) : Promise.resolve({ groups: [] })),
    [projectId, environmentId],
    environmentId,
    { key: `project:${projectId}:env:${environmentId}:error-groups-panel` },
  );
  const [filter, setFilter] = useState<ErrorGroupStatus>('open');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const all = errorGroups.data?.groups ?? [];
  const groups = all.filter((group) => group.status === filter);
  const counts = { open: 0, resolved: 0, muted: 0 } as Record<ErrorGroupStatus, number>;
  for (const group of all) counts[group.status] = (counts[group.status] ?? 0) + 1;

  async function setStatus(group: ApplicationErrorGroup, status: ErrorGroupStatus) {
    setBusyId(group.id);
    setError(null);
    try {
      await wordpressApi.setErrorGroupStatus(group.id, status);
      errorGroups.reload();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusyId(null);
    }
  }

  const logs = project.wordpressSite.data?.inspection?.capabilities.logs;

  return (
    <PanelCard labelledBy="wp-errors-title" testId="wp-error-groups">
      <SectionHeader id="wp-errors-title" title="Errors" />
      {logs && logs !== 'full' ? (
        <p className="panel__note muted">
          {logs === 'none'
            ? 'No error log is readable on this host, so errors are not collected. Enable a PHP error log (or WP_DEBUG_LOG) for this site.'
            : 'Only some logs are readable on this host, so errors may be missing.'}
        </p>
      ) : null}
      {/* Filters, not tabs: one list, narrowed by status. Counts appear once the list has loaded. */}
      <FilterChips
        label="Show errors by status"
        value={filter}
        onChange={setFilter}
        options={(['open', 'resolved', 'muted'] as ErrorGroupStatus[]).map((status) => ({
          value: status,
          label: labelFor(errorGroupStatusLabel, status),
          count: errorGroups.data ? counts[status] : undefined,
        }))}
      />
      <ErrorNotice error={error} />
      {errorGroups.error && !errorGroups.data ? (
        <ErrorNotice error={errorGroups.error} onRetry={errorGroups.reload} nextStep="The errors of this environment could not be read, so none is shown as absent." />
      ) : errorGroups.loading && all.length === 0 ? (
        <LoadingRows label="Loading errors" rows={3} rowHeight="40px" />
      ) : groups.length === 0 ? (
        <QuietEmpty testId="wp-no-errors">
          {logs === 'none'
            ? 'Errors not collected: no readable log'
            : filter === 'open'
              ? logs === 'partial'
                ? 'No open errors · partial logs'
                : 'No open errors'
              : `No ${labelFor(errorGroupStatusLabel, filter).toLowerCase()} errors`}
        </QuietEmpty>
      ) : (
        <ul className="wp-errors">
          {groups.map((group) => (
            <li key={group.id} className="wp-errors__item">
              <div className="wp-errors__head">
                <LevelBadge level={levelOf[group.level] ?? 'warning'}>{labelFor(errorCategoryLabel, group.category)}</LevelBadge>
                {componentLabel(group.component) ? <span className="muted">{componentLabel(group.component)}</span> : null}
                <span className="muted tabular-nums">
                  {group.occurrences} × · last {relativeTime(group.last_seen_at)} · first {relativeTime(group.first_seen_at)}
                </span>
              </div>
              <p className="wp-errors__message">
                <code>{group.message}</code>
              </p>
              {group.file ? (
                <p className="muted wp-errors__where">
                  <code>
                    {group.file}
                    {group.line ? `:${group.line}` : ''}
                  </code>
                  {group.route ? <> · {group.route}</> : null}
                </p>
              ) : null}
              {group.correlation_note ? (
                <p className="wp-errors__correlation">
                  {group.correlation_note}
                  {group.correlated_execution_id ? (
                    <>
                      {' '}
                      <Link to={project.sectionPath('deployments', { run: group.correlated_execution_id, view: 'runs' })}>View details</Link>
                    </>
                  ) : null}
                </p>
              ) : null}
              <div className="pill-list">
                {group.status !== 'resolved' ? (
                  <button type="button" className="button button--secondary button--small" disabled={busyId === group.id} onClick={() => setStatus(group, 'resolved')}>
                    Mark resolved
                  </button>
                ) : null}
                {group.status !== 'muted' ? (
                  <button type="button" className="button button--ghost button--small" disabled={busyId === group.id} onClick={() => setStatus(group, 'muted')}>
                    Mute
                  </button>
                ) : null}
                {group.status !== 'open' ? (
                  <button type="button" className="button button--ghost button--small" disabled={busyId === group.id} onClick={() => setStatus(group, 'open')}>
                    Reopen
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </PanelCard>
  );
}
